import React from 'react';

import { StepProps, useMutation } from '@betterment/js-runtime';
import { Box, Button, Label, Text, Tooltip } from '@betterment/samba-react';
import { CenteredWorkflowLayout, MaskedInput, MaskedInputProps } from '@betterment/style-closet-react';
import { Form, Formik, useField } from 'formik';
import pick from 'lodash/pick';
import * as Yup from 'yup';

import UserCreationSignupFlowHeader from './UserCreationSignupFlowHeader';

import Footer from '../../Shared/components/Footer';
import { RedirectToPreviousStep } from '../../Shared/components/RedirectToPreviousStep';
import SecureInfoBadge from '../../Shared/components/SecureInfoBadge';
import { withoutOutline } from '../../Shared/components/WithoutOutline';
import { FormikErrorMessage } from '../../Shared/components-v2/FormErrorGuide/FormErrorGuide';
import { PhoneNumberSchema } from '../../Shared/phone-number-schema';
import { urlBuilder } from '../../Shared/urlBuilder';
import { Values } from '../data';
import { convertApiErrorsToFormikMsg, overridePhoneNumberErrorMessage } from '../form-helpers';

interface FormikMaskedInputProps extends Partial<MaskedInputProps> {
  name: string;
  id: string;
  type: string;
  variant: 'taxId' | 'phone' | 'money';
}

export const FormikMaskedInput: React.FC<React.PropsWithChildren<FormikMaskedInputProps>> = ({
  name,
  type,
  ...rest
}) => {
  const [field] = useField({ name, type });
  return <MaskedInput {...field} {...rest} />;
};

export const FormikMaskedField = withoutOutline(FormikMaskedInput);

const StepSchema = Yup.object<Partial<Values>>().shape({
  phoneNumber: PhoneNumberSchema,
});

const PhoneNumber: React.FC<React.PropsWithChildren<StepProps<Values>>> = ({ flowState, goToNextStep }) => {
  const [validatePhoneNumber] = useMutation(urlBuilder.apiV2ValidatePhoneNumber.absolute());

  if (!flowState.values.legalFirstName || !flowState.values.lastName) {
    return <RedirectToPreviousStep />;
  }

  const validate = async ({ phoneNumber }: Partial<Values>) => {
    const { errors } = await validatePhoneNumber({ phoneNumber });
    if (errors) {
      return convertApiErrorsToFormikMsg(errors, { overrideErrorMessage: overridePhoneNumberErrorMessage });
    }
    return undefined;
  };

  return (
    <CenteredWorkflowLayout variant="five-column" title="Phone Number" heading="What’s your phone number?">
      <CenteredWorkflowLayout.Slot name="header">
        <UserCreationSignupFlowHeader />
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot>
        <Formik
          onSubmit={goToNextStep}
          validationSchema={StepSchema}
          validate={validate}
          initialValues={pick(flowState.values, Object.keys(StepSchema.getDefault()))}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ errors, isSubmitting }) => (
            <Form>
              <Box css={{ marginBottom: 'space.400' }}>
                <Box css={{ display: 'flex', alignItems: 'center', marginBottom: 'space.100' }}>
                  <Label htmlFor="phoneNumber" css={{ marginRight: 'space.50' }}>
                    U.S. phone number
                  </Label>
                  <Tooltip>
                    <Tooltip.Trigger asChild={true}>
                      <Tooltip.IconButton />
                    </Tooltip.Trigger>
                    <Tooltip.Content align="start" side="left">
                      <Tooltip.Text>
                        Must be a U.S. phone number. By providing your number to Betterment, you agree Betterment may
                        contact you by phone regarding opening, funding, and/or to otherwise service an account. You may
                        request to be put on a "Do Not Call" list maintained by Betterment.
                      </Tooltip.Text>
                    </Tooltip.Content>
                  </Tooltip>
                </Box>
                <FormikMaskedField
                  name="phoneNumber"
                  id="phoneNumber"
                  inputMode="decimal"
                  variant="phone"
                  autoFocus={!flowState.values.phoneNumber}
                  fullWidth={true}
                  aria-required={true}
                  aria-describedby={errors.phoneNumber ? 'phoneNumber-error' : undefined}
                />
                <FormikErrorMessage name="phoneNumber" id="phoneNumber-error" />
                <Text as="p" color="text.weak" css={{ marginTop: 'space.50' }}>
                  We'll use this to secure your account with two-factor authentication
                </Text>
              </Box>
              <Button disabled={isSubmitting} type="submit">
                Continue
              </Button>
              <Box css={{ marginTop: 'space.200' }}>
                <SecureInfoBadge />
              </Box>
            </Form>
          )}
        </Formik>
      </CenteredWorkflowLayout.Slot>
      <CenteredWorkflowLayout.Slot name="footer">
        <Footer />
      </CenteredWorkflowLayout.Slot>
    </CenteredWorkflowLayout>
  );
};

export default PhoneNumber;
